import {
  API_PREFIX,
  buildPath,
  ErrorResponseSchema,
  routes,
  type RouteName,
  type RouteResponse,
  type Routes,
} from '@lia/contracts';
import type { z } from 'zod';

// Typed client derived from the contracts route registry. Same-origin relative `/api` path; the session is an
// opaque HttpOnly cookie managed by the server, so the browser sends it automatically (credentials: same-origin).
// No auth token is ever stored in JavaScript-accessible storage.

export type ApiErrorKind = 'unauthorized' | 'forbidden' | 'not_found' | 'rate_limited' | 'validation' | 'conflict' | 'server' | 'network';

export class ApiError extends Error {
  constructor(
    readonly kind: ApiErrorKind,
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';
  if (status === 400 || status === 422) return 'validation';
  if (status === 409) return 'conflict';
  return 'server';
}

const FALLBACK_MESSAGES: Record<ApiErrorKind, string> = {
  unauthorized: 'Tu sesión expiró. Inicia sesión de nuevo.',
  forbidden: 'No tienes permiso para realizar esta acción.',
  not_found: 'No encontramos este recurso o no tienes acceso a él.',
  rate_limited: 'Demasiadas solicitudes. Intenta de nuevo en unos minutos.',
  validation: 'Revisa los datos enviados.',
  conflict: 'La operación entra en conflicto con el estado actual.',
  server: 'Ocurrió un error en el servidor.',
  network: 'No pudimos conectar con el servidor.',
};

/** Parse any non-2xx response into a typed ApiError using the shared error envelope. */
export async function toApiError(res: Response): Promise<ApiError> {
  const kind = kindForStatus(res.status);
  let code: string = kind;
  let message = FALLBACK_MESSAGES[kind];
  let details: unknown;
  try {
    const parsed = ErrorResponseSchema.safeParse(await res.json());
    if (parsed.success) {
      code = parsed.data.error.code;
      message = parsed.data.error.message || message;
      details = parsed.data.error.details;
    }
  } catch {
    // Non-JSON error body: keep the fallback message.
  }
  return new ApiError(kind, res.status, code, message, details);
}

type In<T> = T extends z.ZodType ? z.input<T> : never;
export type Input<N extends RouteName> = {
  params?: In<NonNullable<Routes[N]['params']>>;
  query?: Partial<In<NonNullable<Routes[N]['query']>>>;
  body?: In<NonNullable<Routes[N]['body']>>;
};

/** Listeners notified on 401 so the app can redirect to sign-in once. */
const unauthorizedListeners = new Set<() => void>();
export function onUnauthorized(fn: () => void): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

/** Session requirements signalled by the API with 403 + a specific code. */
export type AuthRequirement = 'mfa_required' | 'mfa_enrollment_required';
const authRequirementListeners = new Set<(req: AuthRequirement) => void>();
export function onAuthRequirement(fn: (req: AuthRequirement) => void): () => void {
  authRequirementListeners.add(fn);
  return () => authRequirementListeners.delete(fn);
}

export function toQueryString(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function routeUrl<N extends RouteName>(name: N, input: { params?: Record<string, string | number>; query?: Record<string, unknown> } = {}): string {
  const def = routes[name];
  return API_PREFIX + buildPath(def.path, input.params ?? {}) + toQueryString(input.query);
}

/**
 * Embed token of the HubSpot calling page (plan B6). That page runs in a third-party iframe where the session cookie
 * is not sent; it signs in through a first-party popup and keeps a short-lived token IN MEMORY ONLY (never storage).
 * While set, requests carry it as a bearer token and omit cookies. The API accepts it on the embed routes only.
 */
let bearer: string | null = null;
export function setApiBearer(token: string | null): void {
  bearer = token;
}

export async function rawFetch(url: string, init: RequestInit = {}): Promise<Response> {
  let res: Response;
  const auth: RequestInit = bearer ? { credentials: 'omit', headers: { ...(init.headers as Record<string, string> | undefined), Authorization: `Bearer ${bearer}` } } : {};
  try {
    res = await fetch(url, { credentials: 'same-origin', ...init, ...auth });
  } catch {
    throw new ApiError('network', 0, 'network', FALLBACK_MESSAGES.network);
  }
  if (!res.ok) {
    const err = await toApiError(res);
    if (err.kind === 'unauthorized') unauthorizedListeners.forEach((fn) => fn());
    if (err.kind === 'forbidden' && (err.code === 'mfa_required' || err.code === 'mfa_enrollment_required')) {
      const code = err.code;
      authRequirementListeners.forEach((fn) => fn(code));
    }
    throw err;
  }
  return res;
}

export async function api<N extends RouteName>(name: N, args?: Input<N>): Promise<RouteResponse<N>> {
  const input = (args ?? {}) as { params?: Record<string, string | number>; query?: Record<string, unknown>; body?: unknown };
  const def = routes[name];
  const init: RequestInit = { method: def.method, headers: { Accept: 'application/json' } };
  if (input.body !== undefined) {
    init.body = JSON.stringify(input.body);
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
  }
  const res = await rawFetch(routeUrl(name, input), init);
  if (res.status === 204) return undefined as RouteResponse<N>;
  return (await res.json()) as RouteResponse<N>;
}

/** Extract a filename from a Content-Disposition header. */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

/** Download a file endpoint (e.g. CSV export) with the same auth/errors as the API client. */
export async function downloadFile(url: string, fallbackName: string): Promise<string> {
  const res = await rawFetch(url, { headers: { Accept: 'text/csv' } });
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get('content-disposition'), fallbackName);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return name;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Ocurrió un error inesperado.';
}
